import { createTheme } from '@mui/material/styles';
import { defaultTheme } from 'react-admin';
import { alpha } from '@mui/material/styles';

import variables from './variables';

const theme = {
	...defaultTheme,
	typography: {
		fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
		fontSize: 14,
		h1: {
			fontSize: '2rem',
			fontWeight: 700,
			letterSpacing: '-0.02em',
		},
		h2: {
			fontSize: '1.75rem',
			fontWeight: 600,
			letterSpacing: '-0.01em',
		},
		h3: {
			fontSize: '1.5rem',
			fontWeight: 600,
			letterSpacing: '-0.01em',
		},
		h4: {
			fontSize: '1.25rem',
			fontWeight: 600,
		},
		h5: {
			fontSize: '1.125rem',
			fontWeight: 600,
		},
		h6: {
			fontSize: '1rem',
			fontWeight: 600,
		},
		subtitle1: {
			fontSize: '1rem',
			fontWeight: 500,
		},
		subtitle2: {
			fontSize: '0.875rem',
			fontWeight: 500,
		},
		body1: {
			fontSize: '1rem',
			fontWeight: 400,
			lineHeight: 1.6,
		},
		body2: {
			fontSize: '0.875rem',
			fontWeight: 400,
			lineHeight: 1.5,
		},
		button: {
			fontSize: '0.875rem',
			fontWeight: 600,
			textTransform: 'none',
		},
		caption: {
			fontSize: '0.75rem',
			fontWeight: 400,
			lineHeight: 1.4,
		},
		overline: {
			fontSize: '0.75rem',
			fontWeight: 600,
			letterSpacing: '0.08em',
			textTransform: 'uppercase',
		},
	},
	components: {
		...defaultTheme.components,
		MuiAppBar: {
			styleOverrides: {
				root: {
					boxShadow: '0px 1px 30px 1px rgba(0, 0, 0, 0.11)',
					zIndex: 1300
				}
			}
		},
		MuiPaper: {
			styleOverrides: {
				root: {
					boxShadow: 'none',
					backgroundImage: 'none'
				},
				elevation1: ({ theme }) => ({
					border: `1px solid ${theme.palette.divider}`,
				}),
				elevation8: ({ theme }) => ({
					border: `1px solid ${theme.palette.divider}`,
				}),
			},
		},
		MuiList: {
			styleOverrides: {
				root: {
					padding: 0
				}
			}
		},
		MuiDrawer: {
			styleOverrides: {
				docked: {
					marginRight: '19px',
					boxShadow: 'none !important',
					'& .footer-menu': {
						display: 'none'
					},
					'& .RaMenu-open': {
						height: 'calc(100% - 48px)',
						display: 'flex',
						flexDirection: 'column',
						'& .footer-menu': {
							display: 'flex',
							flexDirection: 'column',
							alignItems: 'center',
							margin: '5px',
							justifySelf: 'flex-end',
							alignSelf: 'center',
							marginTop: 'auto'
						}
					},
					'& .RaMenu-closed': {
						width: '55px !important'
					}
				}
			}
		},
		RaSidebar: {
			styleOverrides: {
				root: ({ theme }) => ({
					'& .RaSidebar-fixed': {
						boxShadow: 'none !important'
					},
					'& .RaSidebar-paperAnchorLeft .RaSidebar-fixed': {
						borderRight: `1px solid ${theme.palette.divider} !important`
					},
					'& .MuiMenuItem-root': {
						borderRadius: '6px',
						margin: '2px 8px',
						padding: '6px 8px',
						fontSize: '15px'
					}
				})
			}
		},
		MuiFormControl: {
			defaultProps: {
				variant: 'outlined',
				size: 'small'
			},
			styleOverrides: {
				root: {
					'.RaCreate-main &, .RaEdit-main &': {
						minWidth: '250px !important'
					}
				},
				fullWidth: {
					maxWidth: '50%',
					'@media (max-width: 600px)': {
						maxWidth: '100%'
					}
				}
			}
		},
		MuiAutocomplete: {
			styleOverrides: {
				root: {
					'.RaCreate-main &, .RaEdit-main &': {
						minWidth: '250px !important'
					}
				}
			}
		},
		RaSelectInput: {
			styleOverrides: {
				root: {
					'.RaCreate-main &, .RaEdit-main &': {
						minWidth: '250px !important'
					}
				}
			}
		},
		MuiTextField: {
			defaultProps: {
				variant: 'outlined',
				size: 'small'
			},
		},
		MuiSnackbarContent: {
			styleOverrides: {
				message: {
					whiteSpace: 'pre-wrap'
				}
			}
		},
		MuiMenuItem: {
			styleOverrides: {
				root: {
					'@media (min-width: 600px)': {
						minHeight: '36px'
					}
				}
			}
		},
		MuiInputBase: {
			defaultProps: {
				autoComplete: 'off'
			}
		},
		MuiCardHeader: {
			styleOverrides: {
				root: {
					borderBottom: '1px solid #DADCE0',
				},
				title: {
					fontSize: '22px'
				}
			}
		},
		RaRichTextInput: {
			styleOverrides: {
				root: {
					maxWidth: '100%',
					'& .RaRichTextInput-editorContent': {
						'& > div': {
							minHeight: '200px',
						}
					},
					'& .RaRichTextInput-editorContent img': {
						maxWidth: '100%'
					},
					'& .RaRichTextInputToolbar-root': {
						alignSelf: 'flex-start'
					},
					'& .ProseMirror': {
						borderColor: '#000 !important'
					},
					'& .tiptap': {
						'ul[data-type="taskList"]': {
							listStyle: 'none',
							marginLeft: 0,
							padding: 0,

							'li': {
								alignItems: 'flex-start',
								display: 'flex',

								'> label': {
									flex: '0 0 auto',
									marginRight: '0.5rem',
									userSelect: 'none'
								},

								'> div': {
									flex: '1 1 auto'
								}
							},

							'input[type="checkbox"]': {
								cursor: 'pointer'
							},

							'ul[data-type="taskList"]': {
								margin: 0
							}
						}
					}
				}
			}
		},
		MuiTypography: {
			styleOverrides: {
				root: {
					'& ul[data-type="taskList"]': {
						listStyle: 'none',
						marginLeft: 0,
						padding: 0,
						pointerEvents: 'none',

						'li': {
							alignItems: 'flex-start',
							display: 'flex',

							'> label': {
								flex: '0 0 auto',
								marginRight: '0.5rem',
								userSelect: 'none'
							},

							'> div': {
								flex: '1 1 auto'
							},
							'& p': {
								margin: 0
							}
						},

						'input[type="checkbox"]': {
							cursor: 'pointer'
						},

						'ul[data-type="taskList"]': {
							margin: 0
						}
					}
				}
			}
		},
		MuiButton: {
			styleOverrides: {
				root: {
					textTransform: 'none',
					fontWeight: 600,
					letterSpacing: '0.01em'
				},
				contained: {
					boxShadow: 'none',
					'&:hover, &:active': {
						boxShadow: 'none'
					}
				}
			}
		},
		RaDatagrid: {
			styleOverrides: {
				root: {
					'& tbody .MuiTableRow-root:last-child .MuiTableCell-root': {
						border: 0
					}
				}
			}
		},
		MuiCssBaseline: {
			styleOverrides: {
				'.rich-text': {
					'& p:empty': {
						height: '1em'
					},
					'& img': {
						maxWidth: '100%'
					}
				},
				'.recharts-wrapper *:focus': {
					outline: 'none'
				}
			}
		},
		RaInPlaceEditor: {
			styleOverrides: {
				root: {
					minHeight: '20px',
					'& .RaInPlaceEditor-reading': {
						minHeight: 'inherit'
					},
					'& .RaInPlaceEditor-reading div, & .RaInPlaceEditor-editing input, & .RaInPlaceEditor-editing textarea, & .RaInPlaceEditor-saving div': {
						fontSize: '0.875rem',
						marginBottom: 0
					}
				}
			}
		},
		RaList: {
			styleOverrides: {
				root: {
					'& .RaList-actions': {
						marginBottom: '8px'
					}
				}
			}
		}
	}
};

const lightTheme = createTheme({
	palette: {
		...theme.palette,
		primary: {
			main: variables.colors.primary
		},
		secondary: {
			main: '#7C3AED',
			light: '#8B5CF6',
			dark: '#6D28D9',
			contrastText: '#ffffff',
		},
		error: {
			main: '#E11D48',
			light: '#F43F5E',
			dark: '#BE123C',
			contrastText: '#ffffff',
		},
		warning: {
			main: '#D97706',
			light: '#F59E0B',
			dark: '#B45309',
			contrastText: '#ffffff',
		},
		info: {
			main: '#0284C7',
			light: '#38BDF8',
			dark: '#0369A1',
			contrastText: '#ffffff',
		},
		success: {
			main: '#059669',
			light: '#10B981',
			dark: '#047857',
			contrastText: '#ffffff',
		},
		divider: '#E0E0E0'
	},
	typography: theme.typography,
	shadows: [
		"none",
		"0 1px 15px 1px rgba(0, 0, 0, 0.11)",
		"0px 3px 1px -2px rgba(0,0,0,0.2),0px 2px 2px 0px rgba(0,0,0,0.14),0px 1px 5px 0px rgba(0,0,0,0.12)",
		"0px 3px 3px -2px rgba(0,0,0,0.2),0px 3px 4px 0px rgba(0,0,0,0.14),0px 1px 8px 0px rgba(0,0,0,0.12)",
		"0px 2px 4px -1px rgba(0,0,0,0.2),0px 4px 5px 0px rgba(0,0,0,0.14),0px 1px 10px 0px rgba(0,0,0,0.12)",
		"0px 3px 5px -1px rgba(0,0,0,0.2),0px 5px 8px 0px rgba(0,0,0,0.14),0px 1px 14px 0px rgba(0,0,0,0.12)",
		"0px 3px 5px -1px rgba(0,0,0,0.2),0px 6px 10px 0px rgba(0,0,0,0.14),0px 1px 18px 0px rgba(0,0,0,0.12)",
		"0px 4px 5px -2px rgba(0,0,0,0.2),0px 7px 10px 1px rgba(0,0,0,0.14),0px 2px 16px 1px rgba(0,0,0,0.12)",
		"0px 5px 5px -3px rgba(0,0,0,0.2),0px 8px 10px 1px rgba(0,0,0,0.14),0px 3px 14px 2px rgba(0,0,0,0.12)",
		"0px 5px 6px -3px rgba(0,0,0,0.2),0px 9px 12px 1px rgba(0,0,0,0.14),0px 3px 16px 2px rgba(0,0,0,0.12)",
		"0px 6px 6px -3px rgba(0,0,0,0.2),0px 10px 14px 1px rgba(0,0,0,0.14),0px 4px 18px 3px rgba(0,0,0,0.12)",
		"0px 6px 7px -4px rgba(0,0,0,0.2),0px 11px 15px 1px rgba(0,0,0,0.14),0px 4px 20px 3px rgba(0,0,0,0.12)",
		"0px 7px 8px -4px rgba(0,0,0,0.2),0px 12px 17px 2px rgba(0,0,0,0.14),0px 5px 22px 4px rgba(0,0,0,0.12)",
		"0px 7px 8px -4px rgba(0,0,0,0.2),0px 13px 19px 2px rgba(0,0,0,0.14),0px 5px 24px 4px rgba(0,0,0,0.12)",
		"0px 7px 9px -4px rgba(0,0,0,0.2),0px 14px 21px 2px rgba(0,0,0,0.14),0px 5px 26px 4px rgba(0,0,0,0.12)",
		"0px 8px 9px -5px rgba(0,0,0,0.2),0px 15px 22px 2px rgba(0,0,0,0.14),0px 6px 28px 5px rgba(0,0,0,0.12)",
		"0px 8px 10px -5px rgba(0,0,0,0.2),0px 16px 24px 2px rgba(0,0,0,0.14),0px 6px 30px 5px rgba(0,0,0,0.12)",
		"0px 8px 11px -5px rgba(0,0,0,0.2),0px 17px 26px 2px rgba(0,0,0,0.14),0px 6px 32px 5px rgba(0,0,0,0.12)",
		"0px 9px 11px -5px rgba(0,0,0,0.2),0px 18px 28px 2px rgba(0,0,0,0.14),0px 7px 34px 6px rgba(0,0,0,0.12)",
		"0px 9px 12px -6px rgba(0,0,0,0.2),0px 19px 29px 2px rgba(0,0,0,0.14),0px 7px 36px 6px rgba(0,0,0,0.12)",
		"0px 10px 13px -6px rgba(0,0,0,0.2),0px 20px 31px 3px rgba(0,0,0,0.14),0px 8px 38px 7px rgba(0,0,0,0.12)",
		"0px 10px 13px -6px rgba(0,0,0,0.2),0px 21px 33px 3px rgba(0,0,0,0.14),0px 8px 40px 7px rgba(0,0,0,0.12)",
		"0px 10px 14px -6px rgba(0,0,0,0.2),0px 22px 35px 3px rgba(0,0,0,0.14),0px 8px 42px 7px rgba(0,0,0,0.12)",
		"0px 11px 14px -7px rgba(0,0,0,0.2),0px 23px 36px 3px rgba(0,0,0,0.14),0px 9px 44px 8px rgba(0,0,0,0.12)",
		"0px 11px 15px -7px rgba(0,0,0,0.2),0px 24px 38px 3px rgba(0,0,0,0.14),0px 9px 46px 8px rgba(0,0,0,0.12)"
	],
	components: {
		...theme.components,
		RaLayout: {
			styleOverrides: {
				root: {
					backgroundColor: alpha(variables.colors.primary, 0.025),
					'& .RaLayout-content': {
						backgroundColor: 'transparent'
					}
				},
				content: {
					backgroundColor: 'transparent'
				}
			}
		},
		RaSidebar: {
			styleOverrides: {
				root: ({ theme }) => ({
					'& .MuiMenuItem-root': {
						'&.RaMenuItemLink-active': {
							backgroundColor: 'rgba(0, 0, 0, 0.04)',
							color: variables.colors.primary,
							'& .MuiListItemIcon-root': {
								color: variables.colors.primary
							}
						}
					}
				})
			}
		},
		MuiTableCell: {
			styleOverrides: {
				root: {
					borderColor: '#F6F6F6'
				}
			}
		},
		MuiDivider: {
			styleOverrides: {
				root: {
					borderColor: '#F6F6F6'
				}
			}
		}
	}
}, theme);

const defaultDarkTheme = createTheme({ palette: { mode: 'dark' } });
const darkTheme = createTheme({
	...theme,
	palette: {
		mode: 'dark',
		primary: {
			main: '#9B98B5',
			light: '#B5B3CC',
			dark: '#7D7A96',
			contrastText: '#ffffff',
		},
		secondary: {
			main: '#8B5CF6',
			light: '#A78BFA',
			dark: '#7C3AED',
			contrastText: '#ffffff',
		},
		error: {
			main: '#E86B6B',
			light: '#F39393',
			dark: '#D95555',
			contrastText: '#ffffff',
		},
		warning: {
			main: '#FBBF24',
			light: '#FCD34D',
			dark: '#F59E0B',
			contrastText: '#000000',
		},
		info: {
			main: '#38BDF8',
			light: '#7DD3FC',
			dark: '#0EA5E9',
			contrastText: '#ffffff',
		},
		success: {
			main: '#10B981',
			light: '#34D399',
			dark: '#059669',
			contrastText: '#ffffff',
		},
		background: {
			default: '#0a0e1a',
			paper: '#1a2332',
		},
		text: {
			primary: '#e8eaed',
			secondary: '#9aa0a6',
		},
		divider: '#2d3748',
	},
	typography: theme.typography,
	components: {
		...theme.components,
		MuiFormGroup: {
			styleOverrides: {
				root: {
					color: defaultDarkTheme.palette.text.primary
				}
			}
		},
		MuiCardHeader: {
			styleOverrides: {
				root: {
					borderBottom: `1px solid ${defaultDarkTheme.palette.background.default}`,
				},
				title: {
					fontSize: '22px'
				}
			}
		},
		RaSidebar: {
			styleOverrides: {
				root: ({ theme }) => ({
					'& .RaSidebar-fixed': {
						boxShadow: 'none !important'
					},
					'& .RaSidebar-paperAnchorLeft .RaSidebar-fixed': {
						borderRight: `1px solid ${theme.palette.divider} !important`
					},
					'& .MuiMenuItem-root': {
						borderRadius: '6px',
						margin: '2px 8px',
						padding: '6px 8px',
						fontSize: '15px',
						'& .MuiListItemIcon-root': {
							color: 'rgba(255, 255, 255, 0.7)'
						},
						'&.RaMenuItemLink-active': {
							backgroundColor: 'rgba(255, 255, 255, 0.08)',
							color: '#FFF',
							'& .MuiListItemIcon-root': {
								color: '#FFF'
							}
						}
					}
				})
			}
		},
	}
});

export { lightTheme, darkTheme, theme };
